import React, { useContext, useEffect, useState } from 'react';
import { Container, Table, Button, Row, Col, Input, Form, Label } from 'reactstrap';
import PaginationMaterial from "../../components/PaginationMaterial/PaginationMaterial";
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Header from '../../components/Headers/Header';
import AdminNavbar from '../../components/Navbars/AdminNavbar/AdminNavbar';
import { AuthContext } from '../../context/AuthContext';
import { ContratoStatusReportContext } from '../../context/ContratoStatusReportContext';
import { InstituicaoContext } from '../../context/InstituicaoContext';
import useUser from '../../context/hooks/useUser';
import { createPermissionChecker, PermissionModule } from '../../types/permissions';

const STATUS_OPTIONS = [
    { value: 1, label: "Ativo" },
    { value: 2, label: "Em preparação" },
    { value: 3, label: "Em edição" },
    { value: 4, label: "Em laboratório" },
    { value: 5, label: "Aguardando venda" },
    { value: 6, label: "Concluído" },
    { value: 7, label: "Vendido" },
    { value: 8, label: "Baixado" },
    { value: 9, label: "Expedição" },
    { value: 10, label: "Venda" },
];

const PAGE_SIZE = 10;

const RelatorioStatusContratos = () => {
    const { objUserTk } = useContext(AuthContext);
    const {
        listContratoStatusReport,
        contratoStatusReportPageCount,
        getContratoStatusReportPaged,
        exportContratoStatusReportPdf,
    } = useContext(ContratoStatusReportContext);
    const { listInstituicao, handleGetInstituicaoByEmpresaId } = useContext(InstituicaoContext);
    const { getPermissionsByUser, userPermissions, useLegacyPermissions, modulePermissions } = useUser();
    const hasPermission = createPermissionChecker(useLegacyPermissions, userPermissions, modulePermissions);

    const [loading, setLoading] = useState(true);
    const [dataSearch, setDataSearch] = useState({
        InstituicaoId: 0,
        City: "",
        Description: "",
        StartDate: "",
        EndDate: "",
        StatusConsolidado: 0,
    });

    useEffect(() => {
        setLoading(true);
        handleGetInstituicaoByEmpresaId(objUserTk?.EmpresaId);

        getContratoStatusReportPaged(1, PAGE_SIZE, { EmpresaId: objUserTk?.EmpresaId })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (objUserTk?.UserId) {
                await getPermissionsByUser(objUserTk.UserId);
            }
        };

        fetchPermissions().catch(error => console.error("Erro ao carregar permissões:", error));
    }, [objUserTk?.UserId]);

    const currentFilters = () => ({
        EmpresaId: objUserTk?.EmpresaId,
        InstituicaoId: dataSearch.InstituicaoId || undefined,
        City: dataSearch.City || undefined,
        Description: dataSearch.Description || undefined,
        StartDate: dataSearch.StartDate || undefined,
        EndDate: dataSearch.EndDate || undefined,
        StatusConsolidado: dataSearch.StatusConsolidado || undefined,
    });

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDataSearch({ ...dataSearch, [e.target.name]: e.target.value });
    };

    const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setLoading(true);
        getContratoStatusReportPaged(1, PAGE_SIZE, currentFilters())
            .finally(() => setLoading(false));
    };

    const handleClearFilters = () => {
        setDataSearch({ InstituicaoId: 0, City: "", Description: "", StartDate: "", EndDate: "", StatusConsolidado: 0 });
        setLoading(true);
        getContratoStatusReportPaged(1, PAGE_SIZE, { EmpresaId: objUserTk?.EmpresaId })
            .finally(() => setLoading(false));
    };

    const onChangePagination = (e: any, value: any) => {
        getContratoStatusReportPaged(value, PAGE_SIZE, currentFilters());
    };

    const handleExportPdf = () => {
        exportContratoStatusReportPdf(currentFilters());
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar
                brandText={"Relatório de status dos contratos"}
                search={false}
                titleSearch={""}
            />

            <Container className="mt-5">
                <Form onSubmit={handleSearchSubmit}>
                    <Row className="align-items-center mb-4">
                        <Col xs="12" md="4" lg="3" className="mb-2">
                            <Label className="form-control-label" for="InstituicaoId">Instituição</Label>
                            <Input
                                className="form-control-alternative"
                                type="select"
                                name="InstituicaoId"
                                id="InstituicaoId"
                                value={dataSearch.InstituicaoId}
                                onChange={handleSearchChange}
                            >
                                <option value={0}>Todas</option>
                                {listInstituicao.map((instituicao) => (
                                    <option key={instituicao.id} value={instituicao.id}>
                                        {instituicao.name}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col xs="12" md="4" lg="3" className="mb-2">
                            <Label className="form-control-label" for="City">Cidade</Label>
                            <Input
                                className="form-control-alternative"
                                type="text"
                                name="City"
                                id="City"
                                placeholder="Buscar por cidade"
                                value={dataSearch.City}
                                onChange={handleSearchChange}
                            />
                        </Col>
                        <Col xs="12" md="4" lg="3" className="mb-2">
                            <Label className="form-control-label" for="Description">Turma / Descrição</Label>
                            <Input
                                className="form-control-alternative"
                                type="text"
                                name="Description"
                                id="Description"
                                placeholder="Buscar por turma ou descrição"
                                value={dataSearch.Description}
                                onChange={handleSearchChange}
                            />
                        </Col>
                        <Col xs="12" md="4" lg="3" className="mb-2">
                            <Label className="form-control-label" for="StatusConsolidado">Status</Label>
                            <Input
                                className="form-control-alternative"
                                type="select"
                                name="StatusConsolidado"
                                id="StatusConsolidado"
                                value={dataSearch.StatusConsolidado}
                                onChange={handleSearchChange}
                            >
                                <option value={0}>Todos</option>
                                {STATUS_OPTIONS.map((status) => (
                                    <option key={status.value} value={status.value}>
                                        {status.label}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                        <Col xs="12" md="4" lg="3" className="mb-2">
                            <Label className="form-control-label" for="StartDate">Data Inicial</Label>
                            <Input
                                className="form-control-alternative"
                                type="date"
                                name="StartDate"
                                id="StartDate"
                                value={dataSearch.StartDate}
                                onChange={handleSearchChange}
                            />
                        </Col>
                        <Col xs="12" md="4" lg="3" className="mb-2">
                            <Label className="form-control-label" for="EndDate">Data Final</Label>
                            <Input
                                className="form-control-alternative"
                                type="date"
                                name="EndDate"
                                id="EndDate"
                                value={dataSearch.EndDate}
                                onChange={handleSearchChange}
                            />
                        </Col>
                        <Col xs="12" className="d-flex justify-content-start justify-content-md-end mb-2">
                            <Button style={{ color: "#FFF", backgroundColor: "#585858" }} type="submit" size="m">Buscar</Button>
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#d9534f" }}
                                type="button"
                                size="m"
                                onClick={handleClearFilters}
                            >
                                Limpar
                            </Button>
                        </Col>
                        {hasPermission(PermissionModule.RelatorioContratos, 1) && (
                            <Col xs="12" className="text-right mt-3">
                                <Button
                                    style={{ backgroundColor: '#007bff', color: '#FFF' }}
                                    onClick={handleExportPdf}
                                >
                                    <i className="fa-solid fa-file-pdf mr-2"></i> Exportar PDF
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Form>
                {hasPermission(PermissionModule.RelatorioContratos, 2) ? (
                    <Row className="mt-4">
                        <Col>
                            <Table hover responsive className="table-striped">
                                <thead>
                                    <tr>
                                        <th>Nº Contrato</th>
                                        <th>Instituição</th>
                                        <th>Cidade</th>
                                        <th>Turma</th>
                                        <th>Cursos</th>
                                        <th>Alunos</th>
                                        <th>Data Finalização</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listContratoStatusReport.map(item => (
                                        <tr key={item.contratoId}>
                                            <td>{item.contratoId}</td>
                                            <td>{item.instituicaoName || "-"}</td>
                                            <td>{item.city || "-"}</td>
                                            <td>{item.class || "-"}</td>
                                            <td>{item.cursos?.length ? item.cursos.join(", ") : "-"}</td>
                                            <td>{item.currentStudents ?? "-"}</td>
                                            <td>{item.completionDate || "-"}</td>
                                            <td>{item.statusConsolidadoLabel}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <div className="d-flex justify-content-end mt-3">
                                {contratoStatusReportPageCount > 0 && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <PaginationMaterial
                                            onChange={onChangePagination}
                                            count={contratoStatusReportPageCount}
                                        />
                                    </div>
                                )}
                                {listContratoStatusReport.length === 0 && (
                                    <div style={{ color: "red" }}>NÃO FOI ENCONTRADO NENHUM REGISTRO</div>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row className="mt-4">
                        <Col>
                            <div style={{ color: "red", textAlign: "center" }}>
                                Você não tem permissão para visualizar este relatório.
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>
        </>
    );
};

export default RelatorioStatusContratos;
